import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MOCK_USER } from '../../data/go2LegendeMock';
import {
  LogOut,
  User,
  Lock,
  Bell,
  Copy,
  CheckCircle,
  Eye,
  EyeOff,
  AlertTriangle,
} from 'lucide-react';

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();

  // Personal Information State
  const [firstName, setFirstName] = useState('Aleksa');
  const [lastName, setLastName] = useState('Putniković');
  const [phone, setPhone] = useState('+381 64 123 4567');
  const [birthDate, setBirthDate] = useState('1995-03-15');
  const [city, setCity] = useState('Beograd');
  const [personalInfoSaved, setPersonalInfoSaved] = useState(false);

  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Notifications State
  const [emailOffers, setEmailOffers] = useState(true);
  const [emailPoints, setEmailPoints] = useState(true);
  const [emailPromotions, setEmailPromotions] = useState(false);
  const [newsletter, setNewsletter] = useState(true);
  const [notificationSaved, setNotificationSaved] = useState(false);

  // Referral Code Copy State
  const [codeCopied, setCodeCopied] = useState(false);

  // Delete Account State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Handle Personal Info Save
  const handleSavePersonalInfo = () => {
    setPersonalInfoSaved(true);
    setTimeout(() => setPersonalInfoSaved(false), 3000);
  };

  // Handle Password Change
  const handleChangePassword = () => {
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Trenutna lozinka je obavezna');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Nova lozinka mora imati najmanje 8 karaktera');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Nove lozinke se ne poklapaju');
      return;
    }

    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 3000);
  };

  // Handle Notification Toggle
  const handleNotificationToggle = () => {
    setNotificationSaved(true);
    setTimeout(() => setNotificationSaved(false), 2000);
  };

  // Handle Copy Referral Code
  const handleCopyCode = () => {
    navigator.clipboard.writeText(MOCK_USER.loyalty_referral_code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  // Handle Account Delete
  const handleDeleteAccount = () => {
    if (deleteConfirmText === 'OBRIŠI') {
      // In real app: API call to delete account
      alert('Nalog bi bio obrisan (demo aplikacija)');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center mr-8">
                <img src="/assets/go2-logo.png" alt="Go2 Logo" className="h-8 w-auto" />
              </Link>
              <nav className="hidden md:flex space-x-1">
                <span className="text-gray-400">/</span>
                <Link to="/" className="text-gray-500 hover:text-go2-pink px-2">Go2 Legende</Link>
                <span className="text-gray-400">/</span>
                <span className="text-go2-pink font-medium px-2">Profil</span>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 hidden sm:block">{MOCK_USER.name}</span>
              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 font-bold">
                {MOCK_USER.name.charAt(0)}
              </div>
              <Link to="/" className="text-gray-400 hover:text-gray-600">
                <LogOut className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-go2-pink">Podešavanja naloga</h1>
          <p className="text-gray-600">Upravljaj svojim ličnim podacima i podešavanjima profila.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 sticky top-24">
              {/* User Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-24 h-24 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 font-bold text-3xl mb-4">
                  {MOCK_USER.name.charAt(0)}
                </div>
                <h2 className="font-bold text-lg text-gray-900">{MOCK_USER.name}</h2>
                <p className="text-sm text-gray-500 mb-2">{MOCK_USER.email}</p>
                <div className="bg-go2-blue/10 text-go2-blue rounded-full px-3 py-1 text-xs font-medium mb-2">
                  Svetski putnik
                </div>
                <p className="text-xs text-gray-500">Član od: 2023</p>
              </div>

              {/* Referral Code */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-xs font-medium text-gray-600 mb-2">Tvoj kod za preporuku:</p>
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <span className="text-sm font-mono font-bold text-go2-pink">
                    {MOCK_USER.loyalty_referral_code}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="ml-2 text-gray-500 hover:text-go2-pink transition-colors"
                    title="Kopiraj kod"
                  >
                    {codeCopied ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {codeCopied && (
                  <p className="text-xs text-green-600 mt-1">Kod kopiran!</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-9 space-y-6">
            {/* Section 1: Personal Information */}
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
              <div className="flex items-center mb-6">
                <User className="w-6 h-6 text-go2-pink mr-2" />
                <h2 className="text-xl font-bold text-go2-pink">Lični podaci</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ime</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prezime</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={MOCK_USER.email}
                      disabled
                      className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <Lock className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email adresa ne može biti promenjena</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Datum rođenja</label>
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grad</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={handleSavePersonalInfo}
                  className="bg-go2-pink text-white px-6 py-3 rounded-full font-bold text-sm uppercase hover:bg-brand-600 transition-all"
                >
                  Sačuvaj izmene
                </button>
                {personalInfoSaved && (
                  <div className="flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">Podaci su uspešno sačuvani!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Change Password */}
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
              <div className="flex items-center mb-6">
                <Lock className="w-6 h-6 text-go2-pink mr-2" />
                <h2 className="text-xl font-bold text-go2-pink">Promeni lozinku</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trenutna lozinka</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova lozinka</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimalno 8 karaktera</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Potvrdi novu lozinku</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {passwordError && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {passwordError}
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={handleChangePassword}
                  className="bg-go2-pink text-white px-6 py-3 rounded-full font-bold text-sm uppercase hover:bg-brand-600 transition-all"
                >
                  Promeni lozinku
                </button>
                {passwordSaved && (
                  <div className="flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">Lozinka je uspešno promenjena!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Notifications */}
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
              <div className="flex items-center mb-6">
                <Bell className="w-6 h-6 text-go2-pink mr-2" />
                <h2 className="text-xl font-bold text-go2-pink">Obaveštenja</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Email obaveštenja o novim ponudama</p>
                    <p className="text-sm text-gray-500">Primaj obaveštenja o najnovijim putovanjima i akcijama</p>
                  </div>
                  <button
                    onClick={() => {
                      setEmailOffers(!emailOffers);
                      handleNotificationToggle();
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailOffers ? 'bg-go2-pink' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailOffers ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Email obaveštenja o stanju bodova</p>
                    <p className="text-sm text-gray-500">Primaj ažuriranja o bodovima i nivou lojalnosti</p>
                  </div>
                  <button
                    onClick={() => {
                      setEmailPoints(!emailPoints);
                      handleNotificationToggle();
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailPoints ? 'bg-go2-pink' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailPoints ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Obaveštenja o promotivnim akcijama</p>
                    <p className="text-sm text-gray-500">Ekskluzivni popusti i last minute ponude</p>
                  </div>
                  <button
                    onClick={() => {
                      setEmailPromotions(!emailPromotions);
                      handleNotificationToggle();
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailPromotions ? 'bg-go2-pink' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailPromotions ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-900">Newsletter</p>
                    <p className="text-sm text-gray-500">Mesečni newsletter sa putnim inspiracijama</p>
                  </div>
                  <button
                    onClick={() => {
                      setNewsletter(!newsletter);
                      handleNotificationToggle();
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      newsletter ? 'bg-go2-pink' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        newsletter ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {notificationSaved && (
                <div className="mt-4 flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium">Sačuvano</span>
                </div>
              )}
            </div>

            {/* Section 4: Delete Account - Danger Zone */}
            <div className="bg-white rounded-2xl shadow-sm p-8 border-2 border-red-200">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
                <h2 className="text-xl font-bold text-red-600">Obriši nalog</h2>
              </div>

              <p className="text-gray-700 mb-6">
                Brisanjem naloga trajno gubite sve bodove, nivoe i pogodnosti iz programa Go2 Legende. Ova akcija se ne može poništiti.
              </p>

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-500 text-white px-6 py-3 rounded-full font-bold text-sm uppercase hover:bg-red-600 transition-all"
                >
                  Obriši moj nalog
                </button>
              ) : (
                <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                  <p className="text-sm font-medium text-red-900 mb-4">
                    Da biste potvrdili brisanje naloga, unesite <strong>OBRIŠI</strong> u polje ispod:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Unesite OBRIŠI"
                    className="w-full px-4 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all mb-4"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'OBRIŠI'}
                      className={`px-6 py-3 rounded-full font-bold text-sm uppercase transition-all ${
                        deleteConfirmText === 'OBRIŠI'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Potvrdi brisanje
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="border border-gray-300 text-gray-700 px-6 py-3 rounded-full font-bold text-sm hover:bg-gray-50 transition-all"
                    >
                      Otkaži
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-500 text-sm mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} Go2 Event d.o.o.
          </div>
          <div className="flex space-x-6 text-sm text-gray-500">
            <Link to="/pravila" className="hover:text-go2-pink">Pravila programa</Link>
            <Link to="/faq" className="hover:text-go2-pink">FAQ</Link>
            <a href="mailto:office@go2.rs" className="hover:text-go2-pink">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
